// Purchase Date / sale date: the formula core reads only an ISO 'YYYY-MM-DD' prefix (reilm-be
// flip/dateMath.js parseLocalDate) and treats anything else as "no date" -- silently zero tax and
// interest. The scenario therefore stores ISO only; the box shows the design's M/D/YY.
// String arithmetic and UTC dates only, never local time: nothing here can shift a day with the timezone.
// The one local read is placeholderPurchaseDate's "today", which is meant to be the user's own date.
// See README.md ("Purchase Date and Timeline are ISO dates").
export const SCENARIO_DATE_FIELDS = new Set(['purchase_date', 'sales_date'])

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/
const US_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/

const pad2 = (n) => String(n).padStart(2, '0')

function daysInMonth(year, month) {
  if (month === 2) return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

// Year floor 1000: the core builds `new Date(year, ...)`, which maps a year below 100 to 19xx.
function toIso(year, month, day) {
  if (year < 1000 || year > 9999) return null
  if (month < 1 || month > 12) return null
  if (day < 1 || day > daysInMonth(year, month)) return null
  return `${year}-${pad2(month)}-${pad2(day)}`
}

export function isBlankDate(raw) {
  return raw == null || (typeof raw === 'string' && raw.trim() === '')
}

// Typed or stored text -> 'YYYY-MM-DD', or null when blank, impossible or unrecognised.
// Accepts the design's M/D/YY (two-digit year = 20YY), M/D/YYYY, and ISO -- a stored ISO timestamp
// keeps its literal date part, as the backend's own prefix match does.
export function toIsoDate(raw) {
  if (typeof raw !== 'string') return null
  const text = raw.trim()
  let m = ISO_RE.exec(text)
  if (m) return toIso(Number(m[1]), Number(m[2]), Number(m[3]))
  m = US_RE.exec(text)
  if (m) {
    const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])
    return toIso(year, Number(m[1]), Number(m[2]))
  }
  return null
}

// Stored value -> the box's text: M/D/YY, no leading zeros (sow3-section-lean.27.09.svg, `11/1/26`,
// `3/2/27`). A year outside 2000-2099 keeps all four digits: a typed two-digit year means 20YY, so
// showing 1999 as '99' would turn it into 2099 on the next edit. A value that does not parse is shown
// as it is, so it is never silently lost.
export function formatScenarioDate(value) {
  if (isBlankDate(value)) return ''
  const iso = toIsoDate(String(value))
  if (!iso) return String(value)
  const [year, month, day] = iso.split('-')
  const shownYear = year.startsWith('20') ? year.slice(-2) : year
  return `${Number(month)}/${Number(day)}/${shownYear}`
}

// A loaded property or imported template: any recognised format becomes ISO; blank and
// unrecognised values are left as they are (the box flags the latter, the payload sends null).
export function normalizeScenarioDates(scenario) {
  const next = { ...scenario }
  for (const key of SCENARIO_DATE_FIELDS) {
    if (!(key in next) || isBlankDate(next[key])) continue
    const iso = toIsoDate(String(next[key]))
    if (iso) next[key] = iso
  }
  return next
}

// Timeline (Cong, letter 89 item 5d; workbook Q19 = Q18 + P19 * 30.4): the user types months and the
// sale date is purchase + months x 30.4 days, the fraction dropped as the sheet's date cell shows it
// (11/1/26 + 4.0 -> 3/2/27, the design's own pair). Blank, unparseable or under one day: no sale date.
export const DAYS_PER_TIMELINE_MONTH = 30.4

export function saleDateFromMonths(purchaseDate, months) {
  const purchase = toIsoDate(String(purchaseDate ?? ''))
  const n = typeof months === 'number' ? months : Number.NaN
  if (!purchase || !Number.isFinite(n)) return null
  const days = Math.floor(n * DAYS_PER_TIMELINE_MONTH)
  if (days < 1) return null
  const [year, month, day] = purchase.split('-').map(Number)
  const sale = new Date(Date.UTC(year, month - 1, day + days))
  return toIso(sale.getUTCFullYear(), sale.getUTCMonth() + 1, sale.getUTCDate())
}

// The scenario with sales_date recomputed from purchase_date and holding_period (numbers already
// parsed). The one place the sale date is set: load, import and every edit of either input.
export function withDerivedSaleDate(scenario) {
  return { ...scenario, sales_date: saleDateFromMonths(scenario?.purchase_date, scenario?.holding_period) }
}

// Purchase Date left blank (Cong, letter 104, response 2: "Today()+14 ... as a placeholder value until
// the user inputs their own date"). "Today" is the browser's local calendar date, never toISOString():
// in the US evening that is already tomorrow in UTC. The +14 is done on that date in UTC.
export const PLACEHOLDER_PURCHASE_DAYS = 14

export function placeholderPurchaseDate(now = new Date()) {
  const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + PLACEHOLDER_PURCHASE_DAYS))
  return toIso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate())
}

// What the compute and the derived sale date read: the scenario as stored, or with the placeholder
// purchase date while none is stored. Never written back -- README.md ("Purchase Date placeholder").
export function withPlaceholderPurchaseDate(scenario, placeholderIso) {
  if (!placeholderIso || !isBlankDate(scenario?.purchase_date)) return scenario
  return withDerivedSaleDate({ ...scenario, purchase_date: placeholderIso })
}
